// @file calculates the time in hrs/days/months/yrs between current date and past/future date
import { getUnderscoredCurrentLocale } from '@@/bits/current_locale'
import { __ } from '@@/bits/intl'
import { getTime } from '@@/bits/relative_time'
import strings from '@@/bits/relative_time_strings'
import type { UseTimeAgoMessages } from '@vueuse/core'
import { formatTimeAgo, useNow } from '@vueuse/core'
import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import { computed, toValue } from 'vue'

type RelativeTimeUnits = 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'
type RelativeTimeFormat = 'short' | 'long'
type RelativeTimeLongFormat = 'exact' | 'approximate'

const resolveLocaleData = (format: RelativeTimeFormat, longFormat: RelativeTimeLongFormat) => {
  const localeToUse = getUnderscoredCurrentLocale()
  if (format === 'short') {
    return strings[`${localeToUse}-short`] ?? strings[localeToUse] ?? strings['en-short']
  }
  return localeToUse.includes('en') && longFormat === 'exact'
    ? strings['en-exact']
    : (strings[localeToUse] ?? strings.en)
}

// Renders only the magnitude/unit; the ago/from-now direction is applied by the past/future callbacks.
const getTranslatedTimestamp = (
  format: RelativeTimeFormat,
  time: number,
  keyName: string,
  longFormat: RelativeTimeLongFormat = 'approximate',
): string => {
  const localeData = resolveLocaleData(format, longFormat)
  const stringOrStringFunction = localeData[keyName]
  const uninterpolatedString =
    typeof stringOrStringFunction === 'function' ? stringOrStringFunction(time) : stringOrStringFunction
  return uninterpolatedString.replace(/%d/i, `${Math.round(time)}`)
}

const useRelativeTime = function (
  pastOrFutureDate: string | number | Date,
  format: RelativeTimeFormat,
  longFormat: RelativeTimeLongFormat = 'approximate',
  nowDate?: MaybeRefOrGetter<string | number | Date>,
): ComputedRef<string> {
  const pastOrFutureDateTimestamp = getTime(pastOrFutureDate)
  const pastOrFutureDateObject = new Date(pastOrFutureDate) // snapshot once so direction and magnitude share the same input
  const fallbackNow = nowDate != null ? undefined : useNow({ interval: 30_000 })

  return computed(() => {
    const nowVal = nowDate != null ? getTime(toValue(nowDate)) : getTime(fallbackNow!.value)
    const elapsedTimeInMs = nowVal - pastOrFutureDateTimestamp // future elapsed times are negative

    // Short format carries no ago/from-now words; long format wraps the formatted unit with its prefix/suffix.
    const localeData = resolveLocaleData(format, longFormat)
    const withAgo = (str: string): string =>
      format === 'short' ? str : [localeData.prefixAgo, str, localeData.suffixAgo].filter(Boolean).join(' ')
    const withFromNow = (str: string): string =>
      format === 'short' ? str : [localeData.prefixFromNow, str, localeData.suffixFromNow].filter(Boolean).join(' ')

    const messages: UseTimeAgoMessages<RelativeTimeUnits> = {
      // useTimeAgo skips past/future for justNow, so apply the direction here instead.
      justNow:
        elapsedTimeInMs > 0
          ? withAgo(getTranslatedTimestamp(format, 1, 'seconds', longFormat))
          : withFromNow(getTranslatedTimestamp(format, 1, 'seconds', longFormat)), // use seconds for just now
      past: withAgo,
      future: withFromNow,
      second: (n) => getTranslatedTimestamp(format, n, 'seconds', longFormat), // we only have seconds see relative_time.ts
      minute: (n) => getTranslatedTimestamp(format, n, n > 1 ? 'minutes' : 'minute', longFormat),
      hour: (n) => getTranslatedTimestamp(format, n, n > 1 ? 'hours' : 'hour', longFormat),
      day: (n) => getTranslatedTimestamp(format, n, n > 1 ? 'days' : 'day', longFormat),
      week: () =>
        getTranslatedTimestamp(format, Math.floor(Math.abs(elapsedTimeInMs) / 86_400_000), 'days', longFormat), // convert time elapsed to days rounded down to get number of days since useTimeAgo rounds weeks e.g. 15 days becomes 2 weeks
      month: (n) => getTranslatedTimestamp(format, n, n > 1 ? 'months' : 'month', longFormat),
      year: (n) => getTranslatedTimestamp(format, n, n > 1 ? 'years' : 'year', longFormat),
      invalid: __('Invalid date'),
    }

    return formatTimeAgo(pastOrFutureDateObject, { messages }, nowVal)
  })
}

export { useRelativeTime }
